import { type FC, memo, useCallback, useEffect, useRef, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { useAuthenticate } from '@account-kit/react'
import BaseModal from 'components/BaseModal'
import { TransactionButton } from 'components/Button/Button'
import ErrorView from 'components/ErrorView'
import OtpInput from 'components/Input/OtpInput'
import Spacer from 'components/Spacer'
import { Body } from 'components/Text'
import { FlexDivCol, FlexDivRow } from 'components/layout/flex'
import { AUTO_VERIFY_DELAY } from 'constants/defaults'
import { useAppSelector } from 'state/hooks'
import { selectEmailAddress } from 'state/wallet/selectors'
import logError from 'utils/logError'

type Props = {
	onDismiss(): void
}

const EmailVerification: FC<Props> = memo(({ onDismiss }) => {
	const { t } = useTranslation()
	const [otp, setOtp] = useState('')
	const [error, setError] = useState<string | null>(null)
	const [isPending, setIsPending] = useState(false)
	const [countdown, setCountdown] = useState<number>(30)
	const otpInputRef = useRef<HTMLInputElement>(null)

	useEffect(() => {
		if (countdown <= 0) return
		const timer = setInterval(() => {
			setCountdown((prev) => prev - 1)
		}, 1000)
		return () => clearInterval(timer)
	}, [countdown])

	const { authenticate } = useAuthenticate({
		onSuccess: onDismiss,
		onError: (error) => {
			setOtp('')
			setIsPending(false)
			setError(error.message)
			otpInputRef.current?.focus()
			logError(error)
		},
	})
	const emailAddress = useAppSelector(selectEmailAddress)

	const resendCode = useCallback(() => {
		if (!emailAddress) return
		setOtp('')
		setCountdown(30)
		authenticate({
			email: emailAddress,
			type: 'email',
			emailMode: 'otp',
		})
	}, [emailAddress, authenticate])

	const verifyOtp = useCallback(() => {
		if (!otp || otp.length !== 6 || isPending) return
		try {
			setIsPending(true)
			authenticate({
				type: 'otp',
				otpCode: otp,
			})
		} catch (error) {
			if (!error.message?.includes('fingerprint already exists')) {
				setError(error.message)
			}
			setIsPending(false)
		}
	}, [otp, authenticate, isPending])

	useEffect(() => {
		if (otp.length === 6) {
			const timer = setTimeout(() => {
				verifyOtp()
			}, AUTO_VERIFY_DELAY)
			return () => clearTimeout(timer)
		}
	}, [otp, verifyOtp])

	return (
		<StyledBaseModal title={t('account.email-verification.title')} isOpen onDismiss={onDismiss}>
			<Spacer height={20} />
			<FlexDivCol rowGap="24px">
				<Body color="secondary">
					<Trans
						i18nKey="account.email-verification.copy"
						components={[<Emphasis key="emphasis" />]}
						values={{ emailAddress }}
					/>
				</Body>
				<OtpInput
					ref={otpInputRef}
					value={otp}
					onChange={(value) => {
						setOtp(value)
						setError(null)
					}}
					disabled={isPending}
					inputMode="numeric"
					pattern="[0-9]*"
				/>
				{error && (
					<ErrorView
						message="Invalid code. Please try again."
						messageType="error"
						containerStyle={{ margin: '0', padding: '10px 0', fontSize: '12px' }}
					/>
				)}
				<MessageContainer>{t('account.email-verification.warning')}</MessageContainer>
				<FlexDivRow columnGap="10px">
					<TransactionButton fullWidth onClick={resendCode} disabled={countdown > 0 || isPending}>
						{countdown > 0
							? `Resend Code (${countdown}s)`
							: t('account.email-verification.resend-code')}
					</TransactionButton>
				</FlexDivRow>
			</FlexDivCol>
		</StyledBaseModal>
	)
})

const MessageContainer = styled.div`
	color: ${(props) => props.theme.colors.selectedTheme.newTheme.text.primary};
	background: ${(props) => props.theme.colors.selectedTheme.newTheme.containers.highlight.background};
	flex: none;
	order: 0;
	flex-grow: 0;
	text-align: left;
	font-size: 12px;
	padding: 10px;
	border-radius: 4px;
	cursor: default;
	white-space: pre-line;
`

const Emphasis = styled.span`
	color: ${(props) => props.theme.colors.selectedTheme.newTheme.text.primary};
	font-weight: 600;
`

const StyledBaseModal = styled(BaseModal)`
	[data-reach-dialog-content] {
		width: 430px;
		margin-top: 300px;
	}
`

export default EmailVerification
